/**
 * Voice WebSocket Hook - Native Implementation
 *
 * Manages WebSocket connection using native browser WebSocket API.
 * Handles connection lifecycle, audio transmission, and AI responses.
 */

import { useEffect, useCallback, useRef, useState } from "react";
import { voiceAgentActions, voiceAgentStore } from "../store/voiceAgentStore";
import {
  destroyAudioPlayback,
  getAudioPlayback,
} from "../utils/voice-agent/audioPlayback";

const WEBSOCKET_URL =
  "wss://fibi-voice-agent-878503845739.us-central1.run.app/ws";

const MIN_CHUNKS_FOR_DETECTION: number = 5;

export const ReadyState = {
  CONNECTING: 0,
  OPEN: 1,
  CLOSING: 2,
  CLOSED: 3,
  UNINSTANTIATED: -1,
} as const;

export type ReadyStateType = (typeof ReadyState)[keyof typeof ReadyState];

export interface UseVoiceWebSocketReturn {
  sendAudioChunk: (audioData: Uint8Array | string) => void;
  sendControlMessage: (message: string) => void;
  readyState: ReadyStateType;
  connectionStatus: string;
  isConnected: boolean;
}

export function useVoiceWebSocket(): UseVoiceWebSocketReturn {
  const { status } = voiceAgentStore;
  // const { setStatus } = voiceAgentActions;
  const wsRef = useRef<WebSocket | null>(null);
  const reconnectCountRef = useRef(0);
  const reconnectTimeoutRef = useRef<ReturnType<typeof setTimeout> | undefined>(
    undefined
  );
  const [readyState, setReadyState] = useState<ReadyStateType>(
    ReadyState.UNINSTANTIATED
  );
  const connectFnRef = useRef<(() => void) | null>(null);
  const playbackFlushTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(
    null
  );

  // Sample rate detection state
  const serverSampleRateRef = useRef<number | null>(null);
  const arrivalTimesRef = useRef<number[]>([]);
  const sampleCountsRef = useRef<number[]>([]);
  const lastHeuristicRateRef = useRef<number | null>(null);
  const heuristicConfirmCountRef = useRef<number>(0);

  const MANUAL_SAMPLE_RATE_OVERRIDE: number | null = 24000;
  // Buffer to hold chunks until we confirm sample rate
  const pendingChunksRef = useRef<ArrayBuffer[]>([]);
  const sampleRateConfirmedRef = useRef<boolean>(false);

  const shouldConnect = status === "connected";

  const clearPlaybackFlushTimer = useCallback(() => {
    if (playbackFlushTimeoutRef.current) {
      clearTimeout(playbackFlushTimeoutRef.current);
      playbackFlushTimeoutRef.current = null;
    }
  }, []);

  const requestPlaybackFlush = useCallback(() => {
    playbackFlushTimeoutRef.current = null;

    if (
      !sampleRateConfirmedRef.current &&
      pendingChunksRef.current.length > 0
    ) {
      return;
    }

    const targetRate =
      serverSampleRateRef.current || MANUAL_SAMPLE_RATE_OVERRIDE || 24000;
    const audioPlayback = getAudioPlayback(targetRate);
    audioPlayback.markStreamComplete();
  }, []);

  const schedulePlaybackFlush = useCallback(
    (force = false) => {
      clearPlaybackFlushTimer();

      if (force) {
        requestPlaybackFlush();
        return;
      }

      playbackFlushTimeoutRef.current = setTimeout(() => {
        requestPlaybackFlush();
      }, 500);
    },
    [clearPlaybackFlushTimer, requestPlaybackFlush]
  );

  const pushChunkToPlayback = useCallback(
    (chunk: ArrayBuffer, overrideSampleRate?: number) => {
      const targetRate =
        overrideSampleRate ||
        serverSampleRateRef.current ||
        MANUAL_SAMPLE_RATE_OVERRIDE ||
        24000;
      const audioPlayback = getAudioPlayback(targetRate);
      audioPlayback.addChunk(chunk);
      schedulePlaybackFlush();
    },
    [schedulePlaybackFlush]
  );

  // Connect to WebSocket
  const connect = useCallback(() => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      console.log("WebSocket already connected");
      return;
    }

    try {
      console.log("🔌 Connecting to voice agent...");
      const ws = new WebSocket(WEBSOCKET_URL);
      ws.binaryType = "arraybuffer";
      wsRef.current = ws;
      setReadyState(ReadyState.CONNECTING);

      ws.onopen = () => {
        console.log("🔌 WebSocket connected to voice agent");
        setReadyState(ReadyState.OPEN);
        reconnectCountRef.current = 0;

        // Reset detection state on new connection
        serverSampleRateRef.current = MANUAL_SAMPLE_RATE_OVERRIDE;
        sampleRateConfirmedRef.current = MANUAL_SAMPLE_RATE_OVERRIDE !== null;
        pendingChunksRef.current = [];
        arrivalTimesRef.current = [];
        sampleCountsRef.current = [];
        lastHeuristicRateRef.current = null;
        heuristicConfirmCountRef.current = 0;
        clearPlaybackFlushTimer();

        if (MANUAL_SAMPLE_RATE_OVERRIDE) {
          console.log(
            `🎯 Using MANUAL sample rate override: ${MANUAL_SAMPLE_RATE_OVERRIDE}Hz`
          );
        } else {
          console.log(`🔍 Auto-detecting sample rate...`);
        }

        // Send initial connection message
        const timestamp = new Date();
        ws.send(
          JSON.stringify({
            type: "control",
            data: "session_started",
            timestamp,
          })
        );
      };

      ws.onmessage = (event: MessageEvent) => {
        // Check if it's binary audio data
        if (event.data instanceof ArrayBuffer) {
          const byteLen = event.data.byteLength;
          const int16 = new Int16Array(event.data);

          // Add to heuristic buffers for detection
          arrivalTimesRef.current.push(Date.now());
          sampleCountsRef.current.push(int16.length);

          // DEBUGGING: Log raw chunk info
          console.log(
            `🔍 RAW CHUNK: ${byteLen} bytes, ${int16.length} samples`
          );

          // If sample rate not yet confirmed, run heuristic detection
          if (!sampleRateConfirmedRef.current) {
            try {
              const arrivals = arrivalTimesRef.current;
              const counts = sampleCountsRef.current;

              // Start detection after just 3 chunks to be faster
              if (arrivals.length >= MIN_CHUNKS_FOR_DETECTION) {
                const elapsedMs = arrivals[arrivals.length - 1] - arrivals[0];

                // Need at least 150ms of data for reasonable detection
                if (elapsedMs > 150) {
                  const totalSamples = counts.reduce((s, v) => s + v, 0);
                  const candidateRates = [8000, 16000, 24000, 32000, 48000];
                  let bestRate = candidateRates[0];
                  let bestErr = Infinity;

                  for (const r of candidateRates) {
                    const expectedMs = (totalSamples / r) * 1000;
                    const err = Math.abs(expectedMs - elapsedMs) / elapsedMs;
                    console.log(
                      `   📊 Rate ${r}Hz: expected ${expectedMs.toFixed(
                        1
                      )}ms vs actual ${elapsedMs.toFixed(1)}ms (error: ${(
                        err * 100
                      ).toFixed(1)}%)`
                    );
                    if (err < bestErr) {
                      bestErr = err;
                      bestRate = r;
                    }
                  }

                  // Accept if error is reasonable
                  if (bestErr < 0.15) {
                    if (lastHeuristicRateRef.current === bestRate) {
                      heuristicConfirmCountRef.current += 1;
                    } else {
                      lastHeuristicRateRef.current = bestRate;
                      heuristicConfirmCountRef.current = 1;
                    }

                    // Confirm after 2 consecutive same detections
                    if (heuristicConfirmCountRef.current >= 2) {
                      serverSampleRateRef.current = bestRate;
                      sampleRateConfirmedRef.current = true;

                      console.log(
                        `🔍 ✅ Sample rate CONFIRMED: ${bestRate}Hz (error=${(
                          bestErr * 100
                        ).toFixed(1)}%)`
                      );

                      // Now flush all pending chunks to playback with correct rate
                      console.log(
                        `📦 Flushing ${pendingChunksRef.current.length} pending chunks at ${bestRate}Hz`
                      );

                      for (const chunk of pendingChunksRef.current) {
                        pushChunkToPlayback(chunk, bestRate);
                      }
                      pendingChunksRef.current = [];

                      // Add current chunk too
                      pushChunkToPlayback(event.data, bestRate);
                    } else {
                      console.log(
                        `🔍 Candidate: ${bestRate}Hz (err=${(
                          bestErr * 100
                        ).toFixed(1)}%) - confirming... (${
                          heuristicConfirmCountRef.current
                        }/2)`
                      );
                      // Buffer this chunk until confirmed
                      pendingChunksRef.current.push(event.data);
                    }
                  } else {
                    // Detection not good enough yet, keep buffering
                    pendingChunksRef.current.push(event.data);
                    console.log(
                      `🔍 Detecting... buffered ${
                        pendingChunksRef.current.length
                      } chunks (elapsed: ${elapsedMs.toFixed(0)}ms)`
                    );
                  }
                } else {
                  // Not enough time elapsed, keep buffering
                  pendingChunksRef.current.push(event.data);
                  console.log(
                    `⏳ Buffering chunk ${
                      pendingChunksRef.current.length
                    } (waiting for ${elapsedMs.toFixed(0)}ms/${150}ms)`
                  );
                }
              } else {
                // Not enough chunks yet
                pendingChunksRef.current.push(event.data);
                console.log(
                  `⏳ Buffering chunk ${pendingChunksRef.current.length} (waiting for ${arrivals.length}/3 chunks)`
                );
              }
            } catch (e) {
              console.warn("Heuristic detection failed:", e);
              // On error, assume 24kHz (common for modern TTS)
              serverSampleRateRef.current = 24000;
              sampleRateConfirmedRef.current = true;
              console.log("⚠️ Detection failed, defaulting to 24000Hz");

              for (const chunk of pendingChunksRef.current) {
                pushChunkToPlayback(chunk, 24000);
              }
              pendingChunksRef.current = [];
              pushChunkToPlayback(event.data, 24000);
            }
          } else {
            // Sample rate already confirmed, play normally
            const sampleRate = serverSampleRateRef.current || 24000;

            console.log(
              `📥 Audio chunk: ${byteLen} bytes, ${int16.length} samples @ ${sampleRate}Hz`
            );

            pushChunkToPlayback(event.data, sampleRate);
          }

          return;
        }

        // Handle text messages
        console.log("📥 Received from voice agent:", event.data);

        try {
          const message = JSON.parse(event.data);

          // If server explicitly tells us the sample rate, use it immediately
          if (message && message.sampleRate) {
            const sr = Number(message.sampleRate);
            if (!Number.isNaN(sr) && sr > 0) {
              if (!sampleRateConfirmedRef.current) {
                serverSampleRateRef.current = sr;
                sampleRateConfirmedRef.current = true;
                console.log(`🔍 ✅ Server specified sample rate: ${sr}Hz`);

                // Flush pending chunks with correct rate
                console.log(
                  `📦 Flushing ${pendingChunksRef.current.length} pending chunks at ${sr}Hz`
                );

                for (const chunk of pendingChunksRef.current) {
                  pushChunkToPlayback(chunk, sr);
                }
                pendingChunksRef.current = [];
              }
            }
          }

          if (message.type === "audio_response") {
            console.log("🔊 Received AI audio response (JSON format)");
            if (message.data) {
              const binaryString = atob(message.data);
              const bytes = new Uint8Array(binaryString.length);
              for (let i = 0; i < binaryString.length; i++) {
                bytes[i] = binaryString.charCodeAt(i);
              }
              const sampleRate = serverSampleRateRef.current || 24000;
              pushChunkToPlayback(bytes.buffer, sampleRate);
            }
          } else if (message.type === "transcription") {
            console.log("📝 Transcription:", message.text);
          } else if (message.type === "transcript_batch") {
            // Handle transcript batch
            if (
              Array.isArray(message.transcripts) &&
              message.transcripts.length > 0
            ) {
              const lastItem =
                message.transcripts[message.transcripts.length - 1];
              console.log("📝 Transcript update:", lastItem);

              // Update store with the latest transcript
              voiceAgentActions.setTranscript({
                text: lastItem.transcript,
                role: lastItem.role === "assistant" ? "assistant" : "user",
                itemId: lastItem.itemId,
              });
            }
          } else if (message.type === "error") {
            console.error("❌ Server error:", message.message);
          }

          const controlData =
            typeof message.data === "string" ? message.data.toLowerCase() : "";
          const indicatesAudioEnd =
            message.type === "audio_response_complete" ||
            message.type === "audio_complete" ||
            message.type === "audio_response_end" ||
            message.event === "audio_complete" ||
            message.event === "audio_response_complete" ||
            message.final === true ||
            message.isFinal === true ||
            message.is_final === true ||
            (message.type === "control" &&
              /(?:audio|response|session).*(?:end|complete)/.test(controlData));

          if (indicatesAudioEnd) {
            console.log("🏁 Server indicated audio completion");
            schedulePlaybackFlush(true);
          }
        } catch {
          console.log("📥 Raw message:", event.data);
        }
      };

      ws.onerror = (event: Event) => {
        console.error("❌ WebSocket error:", event);
        setReadyState(ReadyState.CLOSED);
      };

      ws.onclose = (event: CloseEvent) => {
        console.log("🔌 WebSocket disconnected:", event.code, event.reason);
        setReadyState(ReadyState.CLOSED);
        schedulePlaybackFlush(true);

        // Clear pending chunks on disconnect
        pendingChunksRef.current = [];
        sampleRateConfirmedRef.current = false;

        // Handle reconnection - COMPLETELY REMOVED
        // if (status === "connected" && event.code !== 1000) {
        //   reconnectCountRef.current += 1;

        //   if (reconnectCountRef.current <= 10) {
        //     const interval = Math.min(
        //       Math.pow(2, reconnectCountRef.current) * 1000,
        //       10000
        //     );
        //     console.log(
        //       `Will attempt reconnection #${reconnectCountRef.current} in ${interval}ms...`
        //     );

        //     reconnectTimeoutRef.current = setTimeout(() => {
        //       connectFnRef.current?.();
        //     }, interval);
        //   } else {
        //     console.error(
        //       `❌ Failed to reconnect after ${reconnectCountRef.current} attempts`
        //     );
        //     setStatus("error");
        //   }
        // }
      };
    } catch (error) {
      console.error("Failed to create WebSocket:", error);
      setReadyState(ReadyState.CLOSED);
    }
  }, [clearPlaybackFlushTimer, schedulePlaybackFlush, pushChunkToPlayback]);

  // Store connect function in ref
  useEffect(() => {
    connectFnRef.current = connect;
  }, [connect]);

  // Disconnect from WebSocket
  const disconnect = useCallback(() => {
    clearPlaybackFlushTimer();

    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
    }

    if (wsRef.current) {
      console.log("🔌 Disconnecting WebSocket...");
      wsRef.current.close(1000, "Session ended by user");
      wsRef.current = null;
      setReadyState(ReadyState.CLOSED);
    }

    // Clear pending chunks
    pendingChunksRef.current = [];
    sampleRateConfirmedRef.current = false;

    // Stop and cleanup audio playback
    destroyAudioPlayback();
  }, [clearPlaybackFlushTimer]);

  // Connect/disconnect based on session status
  useEffect(() => {
    (async () => {
      if (shouldConnect) {
        connect();
      } else {
        disconnect();
      }
    })();

    return () => {
      disconnect();
    };
  }, [shouldConnect, connect, disconnect]);

  // Keep-alive ping
  useEffect(() => {
    let pingInterval: ReturnType<typeof setInterval> | null = null;

    if (readyState === ReadyState.OPEN && wsRef.current) {
      console.log("⏰ Starting keep-alive ping (2s interval)");
      pingInterval = setInterval(() => {
        if (
          wsRef.current?.readyState === WebSocket.OPEN &&
          voiceAgentStore.speaker === "idle"
        ) {
          wsRef.current.send(JSON.stringify({ type: "ping" }));
        }
      }, 2000);
    }

    return () => {
      if (pingInterval) {
        clearInterval(pingInterval);
        pingInterval = null;
      }
    };
  }, [readyState]);

  // Send audio chunk as binary data
  const sendAudioChunk = useCallback((audioData: Uint8Array | string) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(audioData);

      const byteLength =
        audioData instanceof Uint8Array
          ? audioData.byteLength
          : audioData.length;
      const kbDecimal = (byteLength / 1000).toFixed(1);
      const kib = (byteLength / 1024).toFixed(2);
      console.log(
        `📤 Sent audio chunk: ${byteLength} bytes (${kbDecimal} KB / ${kib} KiB)`
      );
    } else {
      console.warn("⚠️ WebSocket not open, cannot send audio");
    }
  }, []);

  // Send control message
  const sendControlMessage = useCallback((message: string) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(
        JSON.stringify({
          type: "control",
          data: message,
          timestamp: Date.now(),
        })
      );
      console.log(`📤 Sent control message: ${message}`);
    }
  }, []);

  // Connection status string
  const connectionStatusMap: Record<ReadyStateType, string> = {
    [ReadyState.CONNECTING]: "Connecting to voice agent...",
    [ReadyState.OPEN]: "Connected to voice agent",
    [ReadyState.CLOSING]: "Disconnecting...",
    [ReadyState.CLOSED]: "Disconnected",
    [ReadyState.UNINSTANTIATED]: "Not connected",
  };
  const connectionStatus = connectionStatusMap[readyState] || "Unknown";

  return {
    sendAudioChunk,
    sendControlMessage,
    readyState,
    connectionStatus,
    isConnected: readyState === ReadyState.OPEN,
  };
}
